/* eslint-disable react-hooks/exhaustive-deps */
import {useTranslation} from "react-i18next"
import {BasePopup} from "components/popup"
import styles from "./DailyAttendanceDetailsPopup.module.css"
import {get} from "lodash"
import {formatDateOnly, getFullName, getUserPhotoUrl, handleError, roundTwoDigits, toastSuccess} from "helpers"
import moment from "moment-timezone"
import {useCurrentProfilePermissions, useModel, useVisible} from "hooks"
import {AttendanceForm} from "./AttendanceForm"
import {useMutation, useQuery} from "@tanstack/react-query"
import {attendanceService, generalSettingsService} from "services"
import {BaseButton, BaseInput, BaseLoading, SecondaryButton} from "components"
import {useCallback, useMemo, useState} from "react"
import {GeneralSettingField} from "types/settings/general-setting"
import {YesNo} from "types/common"
import {Permissions} from "@edular/permissions"
import {ConfirmPopup} from "uiKit"

type Props = {
    isShow: boolean
    selectedAttendance?: any
    onClose: () => void
}

export function DailyAttendanceDetailsPopup(props: Props) {
    const {isShow, onClose, selectedAttendance} = props
    const timezone = get(selectedAttendance, ["student", "timezone"], moment.tz.guess())
    const {t} = useTranslation(["studentServices"])
    const model = useModel()
    const confirmDeletePopup = useVisible()
    const dateFormat = model.getUserDateFormat()
    const [attendanceByDateDetail, setAttendanceByDateDetail] = useState({
        id: undefined,
        attendedMinutes: 0,
        missedMinutes: 0,
        makeUpMinutes: 0,
        distEdMinutes: 0,
        updatedByUserName: undefined,
        updatedByProfileIdAt: undefined
    })

    const studentInfo = get(selectedAttendance, ["student"])
    const avatarUrl = studentInfo?.photo ? getUserPhotoUrl(studentInfo?.photo, 512) : "/image/DefaultAvatar.png"

    const currentProfilePermissions = useCurrentProfilePermissions()

    const canEditAttendance = currentProfilePermissions.hasPermissions({
        staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Edit]
    })

    const canDeleteAttendance = currentProfilePermissions.hasPermissions({
        staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Delete]
    })

    const {data: dailyAttendanceByDateSetting} = useQuery(
        ["daily-attendance-by-date-setting"],
        async () => {
            const result = await generalSettingsService.getSettings([
                GeneralSettingField.EditDailyAttendanceByDateEnabled
            ])
            return result[0]
        },
        {
            enabled: !!selectedAttendance?.dailyAttendanceByDateId && isShow
        }
    )

    const isEditDailyAttendanceByDateEnabled = useMemo(
        () => dailyAttendanceByDateSetting?.value === YesNo.Yes,
        [dailyAttendanceByDateSetting]
    )

    const getDailyAttendanceByDateQuery = useQuery(
        ["get-daily-attendance-by-date", selectedAttendance, isShow],
        async () => {
            const {data} = await attendanceService.getDailyAttendanceListByDate({
                range: {
                    page: 1,
                    pageSize: 1
                },
                dailyAttendanceByDateIds: [selectedAttendance.dailyAttendanceByDateId]
            })
            return data
        },
        {
            enabled: !!selectedAttendance?.dailyAttendanceByDateId && isShow,
            onSuccess: (result) => {
                setAttendanceByDateDetail({
                    id: get(result, [0, "dailyAttendanceByDateId"]),
                    attendedMinutes: roundTwoDigits(get(result, [0, "attendedHours"], 0) * 60),
                    missedMinutes: roundTwoDigits(get(result, [0, "missedHours"], 0) * 60),
                    makeUpMinutes: roundTwoDigits(get(result, [0, "makeUpHours"], 0) * 60),
                    distEdMinutes: roundTwoDigits(get(result, [0, "distEdHours"], 0) * 60),
                    updatedByUserName: get(result, [0, "updatedByUserName"], undefined),
                    updatedByProfileIdAt: get(result, [0, "updatedByProfileIdAt"], undefined)
                })
            },
            initialData: {data: [], total: 0}
        }
    )

    const updateDailyAttendanceByDateMutation = useMutation(attendanceService.updateDailyAttendanceByDate, {
        onSuccess: () => {
            toastSuccess("Update successfully")
            getDailyAttendanceByDateQuery.refetch()
        },
        onError: (err) => {
            handleError(err, true)
        }
    })

    const deleteDailyAttendanceByDateMutation = useMutation(attendanceService.deleteDailyAttendanceByDate, {
        onSuccess: () => {
            toastSuccess("Delete successfully")
            onClose()
        },
        onError: (err) => {
            handleError(err, true)
        }
    })

    const onUpdateAttendanceByDate = () => {
        updateDailyAttendanceByDateMutation.mutate({
            id: attendanceByDateDetail.id,
            attendedHours: attendanceByDateDetail.attendedMinutes?.toString()?.length
                ? Number(attendanceByDateDetail.attendedMinutes / 60)
                : 0,
            missedHours: attendanceByDateDetail.missedMinutes?.toString()?.length
                ? Number(attendanceByDateDetail.missedMinutes / 60)
                : 0,
            makeUpHours: attendanceByDateDetail.makeUpMinutes?.toString()?.length
                ? Number(attendanceByDateDetail.makeUpMinutes / 60)
                : 0,
            distEdHours: attendanceByDateDetail.distEdMinutes?.toString()?.length
                ? Number(attendanceByDateDetail.distEdMinutes / 60)
                : 0
        })
    }

    const isUpdating =
        updateDailyAttendanceByDateMutation.isLoading ||
        getDailyAttendanceByDateQuery.isFetching ||
        deleteDailyAttendanceByDateMutation.isLoading

    const onConfirmDeleteAttendance = useCallback(() => {
        const attendanceId = attendanceByDateDetail.id

        deleteDailyAttendanceByDateMutation.mutate({
            ids: [attendanceId]
        })
        confirmDeletePopup.close()
    }, [attendanceByDateDetail])

    const renderInput = (payload: {value: number; title: string; onChange: (v: any) => void}) => {
        return (
            <div className={styles.inputNumber}>
                <span className={styles.numberTitle}>{payload.title}</span>
                {isEditDailyAttendanceByDateEnabled && model.isStaffOrAdmin() && canEditAttendance ? (
                    <BaseInput
                        title={payload.title}
                        placeholder=""
                        type="number"
                        min={0}
                        disabledArrow
                        value={payload.value}
                        onChange={payload.onChange}
                    />
                ) : (
                    <span className={styles.numberValue}>{payload.value}</span>
                )}
            </div>
        )
    }

    const renderStudentInfo = () => {
        return (
            <div className={styles.studentWrapper}>
                <div className={styles.imgWrapper}>
                    <img src={avatarUrl} className={styles.avatar} alt="" />
                </div>
                <div className={styles.info}>
                    <div className={styles.studentName}>{getFullName(selectedAttendance)}</div>
                    <div className={styles.flexContainer}>
                        <div className={styles.columnContainer}>
                            <div className={styles.labelContainer}>
                                <span className={styles.idLabel}>{t("popup.userId")}: </span>
                                <span className={styles.idValue}>{studentInfo?.customUserId}</span>
                            </div>
                            <div className={styles.labelContainer}>
                                <span className={styles.idLabel}>{t("popup.enrollmentId")}: </span>
                                <span className={styles.idValue}>{studentInfo?.customProfileId}</span>
                            </div>
                        </div>

                        <div className={styles.attendanceStatsForm}>
                            <div className={styles.attendanceStats} style={{gap: 5}}>
                                {renderInput({
                                    value: attendanceByDateDetail.attendedMinutes,
                                    title: "Attended Minutes",
                                    onChange: (v) =>
                                        setAttendanceByDateDetail({
                                            ...attendanceByDateDetail,
                                            attendedMinutes: v
                                        })
                                })}
                                {renderInput({
                                    value: attendanceByDateDetail.missedMinutes,
                                    title: "Missed Minutes",
                                    onChange: (v) =>
                                        setAttendanceByDateDetail({
                                            ...attendanceByDateDetail,
                                            missedMinutes: v
                                        })
                                })}
                                {renderInput({
                                    value: attendanceByDateDetail.makeUpMinutes,
                                    title: "Makeup Minutes",
                                    onChange: (v) =>
                                        setAttendanceByDateDetail({
                                            ...attendanceByDateDetail,
                                            makeUpMinutes: v
                                        })
                                })}
                                {renderInput({
                                    value: attendanceByDateDetail.distEdMinutes,
                                    title: "DistEd Minutes",
                                    onChange: (v) =>
                                        setAttendanceByDateDetail({
                                            ...attendanceByDateDetail,
                                            distEdMinutes: v
                                        })
                                })}
                            </div>
                            {isEditDailyAttendanceByDateEnabled ? (
                                <div className={styles.saveAttendanceStatsContainer}>
                                    <div className={styles.saveStatsContainer}>
                                        {attendanceByDateDetail.updatedByUserName?.length ? (
                                            <span className={styles.lastEditedBy}>
                                                Last edited by {attendanceByDateDetail.updatedByUserName} at (
                                                {moment(attendanceByDateDetail.updatedByProfileIdAt).format(
                                                    model.getUserDateTimeFormat()
                                                )}
                                                )
                                            </span>
                                        ) : null}
                                        <div className={styles.rowContainer}>
                                            {canDeleteAttendance ? (
                                                <SecondaryButton
                                                    loading={isUpdating}
                                                    disabled={isUpdating && !canDeleteAttendance}
                                                    onClick={confirmDeletePopup.open}
                                                    title={"Delete"}
                                                />
                                            ) : null}
                                            {canEditAttendance ? (
                                                <BaseButton
                                                    loading={isUpdating}
                                                    disabled={isUpdating || !canEditAttendance}
                                                    className={styles.saveButton}
                                                    onClick={onUpdateAttendanceByDate}
                                                    title={t("popup.save")}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const loadAttendancesQuery = useQuery(
        ["load-attendances", selectedAttendance],
        async () => {
            const attendanceIds = (selectedAttendance.attendances || []).map((x) => x.id).filter(Boolean)

            if (!attendanceIds?.length) {
                return []
            }

            const params = {
                attendanceIds,
                includeAttendanceDeletedEvents: model.isStaffOrAdmin(),
                includeDeletedAttendance: model.isStaffOrAdmin()
            }
            const {data: attendanceData} = await attendanceService.getDailyAttendanceList({
                range: {
                    page: 1,
                    pageSize: attendanceIds.length
                },
                ...params
            })
            return attendanceData
        },
        {
            enabled: !!selectedAttendance && isShow,
            initialData: [],
            onError: (err) => {
                handleError(err)
            }
        }
    )

    return (
        <BasePopup
            isShow={isShow}
            onClose={onClose}
            leftIcon="PERSON_FILL"
            width="70vw"
            destroyOnClose
            keyboard={false}>
            <div className={styles.popupWrapper}>
                <p className={styles.title}>
                    Daily Attendance on {formatDateOnly(selectedAttendance?.date, dateFormat)}
                </p>
                <div className={styles.divisor} />
                {renderStudentInfo()}
                <div className={styles.attendanceFormContainer}>
                    {loadAttendancesQuery.data
                        .sort((a, b) => a.id - b.id)
                        .map((attendance, index) => (
                            <AttendanceForm
                                key={attendance.id}
                                attendance={attendance}
                                timezone={timezone}
                                refetch={() => {
                                    getDailyAttendanceByDateQuery.refetch()
                                    loadAttendancesQuery.refetch()
                                }}
                            />
                        ))}
                </div>
            </div>
            <ConfirmPopup
                isVisible={confirmDeletePopup.isVisible}
                title={"Are you sure you want to delete this attendance?"}
                onClose={confirmDeletePopup.close}
                onConfirm={onConfirmDeleteAttendance}
            />
            <BaseLoading isShow={loadAttendancesQuery.isFetching} />
        </BasePopup>
    )
}
